import { useEffect, useState, useCallback } from "react";
import VaultLock from "@/components/VaultLock";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import Modal from "@/components/Modal";
import MediaInput from "@/components/MediaInput";
import MediaViewer from "@/components/MediaViewer";
import HeirAssignmentPicker from "@/components/HeirAssignmentPicker";
import { Plus, Bomb, Pencil, Trash2 } from "lucide-react";

const EMPTY = {
  title: "",
  content: "",
  recipients: "",
  media_type: "text",
  media_data: "",
  media_filename: "",
  media_size: 0,
  assigned_heirs: [],
};

export default function Capsule() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const r = await api.get("/capsule");
    setItems(r.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.capsule_id);
    setForm({
      title: c.title,
      content: c.content || "",
      recipients: (c.recipients || []).join(", "),
      media_type: c.media_type || "text",
      media_data: c.media_data || "",
      media_filename: c.media_filename || "",
      media_size: c.media_size || 0,
      assigned_heirs: c.assigned_heirs || [],
    });
    setViewing(null);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      title: form.title,
      content: form.content,
      recipients: form.recipients.split(",").map((s) => s.trim()).filter(Boolean),
      media_type: form.media_type,
      media_data: form.media_data,
      media_filename: form.media_filename,
      media_size: form.media_size,
      assigned_heirs: form.assigned_heirs,
    };
    try {
      if (editingId) {
        const r = await api.put(`/capsule/${editingId}`, payload);
        setItems((arr) => arr.map((x) => (x.capsule_id === editingId ? r.data : x)));
      } else {
        const r = await api.post("/capsule", payload);
        setItems((arr) => [...arr, r.data]);
      }
      toast.success(t("saved"));
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY);
    } catch {
      toast.error("Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/capsule/${id}`);
    toast.success(t("deleted"));
    setItems((arr) => arr.filter((x) => x.capsule_id !== id));
    if (viewing?.capsule_id === id) setViewing(null);
  };

  return (
    <VaultLock>
    <div data-testid="capsule-page">
      <PageHeader
        title={t("capsule_title")}
        subtitle={t("capsule_subtitle")}
        action={
          <button className="btn-gold" onClick={openCreate} data-testid="capsule-add-btn">
            <Plus className="w-4 h-4" /> {t("capsule_add")}
          </button>
        }
      />

      {items.length === 0 ? (
        <EmptyState>—</EmptyState>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((c) => (
            <button
              key={c.capsule_id}
              onClick={() => setViewing(c)}
              className="glass-card rounded-md p-5 text-left hover:border-gold transition"
              data-testid={`cap-${c.capsule_id}`}
            >
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-md bg-red-900/30 border border-red-900/50">
                  <Bomb className="w-5 h-5 text-gold" strokeWidth={1.4} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-gold-light truncate">{c.title}</div>
                  <div className="text-sm text-muted">
                    {c.recipients?.length || 0} destinatarios
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <Modal
        open={!!viewing && !open}
        onClose={() => setViewing(null)}
        title={viewing?.title || ""}
        testid="cap-view-modal"
      >
        {viewing && (
          <div className="space-y-4">
            {viewing.media_data && <MediaViewer media={viewing} />}
            {viewing.content && (
              <div className="text-gold-light whitespace-pre-wrap font-body leading-relaxed">
                {viewing.content}
              </div>
            )}
            <div>
              <div className="font-label text-faded mb-1">Destinatarios</div>
              <div className="text-muted text-sm break-all">
                {(viewing.recipients || []).join(", ") || "—"}
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-3 border-t border-gold-subtle">
              <button className="btn-ghost" onClick={() => remove(viewing.capsule_id)}>
                <Trash2 className="w-4 h-4" /> {t("delete")}
              </button>
              <button className="btn-gold" onClick={() => openEdit(viewing)}>
                <Pencil className="w-4 h-4" /> {t("edit")}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditingId(null);
        }}
        title={editingId ? t("edit") : t("capsule_add")}
        testid="cap-modal"
      >
        <form
          onSubmit={save}
          className="space-y-4"
          autoComplete="off"
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
        >
          <div>
            <label className="font-label text-faded block mb-2">{t("title")}</label>
            <input
              className="input-field"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="cap-title"
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
            />
          </div>

          <MediaInput
            textLabel={t("content")}
            textValue={form.content}
            onTextChange={(v) => setForm({ ...form, content: v })}
            media={form}
            onMediaChange={(m) => setForm({ ...form, ...m })}
          />

          <div>
            <label className="font-label text-faded block mb-2">
              Destinatarios (emails separados por coma)
            </label>
            <input
              className="input-field"
              value={form.recipients}
              onChange={(e) => setForm({ ...form, recipients: e.target.value })}
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
            />
          </div>

          <div className="divider-gold" />
          <HeirAssignmentPicker
            value={form.assigned_heirs}
            onChange={(arr) => setForm({ ...form, assigned_heirs: arr })}
          />

          <div className="modal-actions">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>{t("cancel")}</button>
            <button type="submit" className="btn-gold" data-testid="cap-save">{t("save")}</button>
          </div>
        </form>
      </Modal>
    </div>
    </VaultLock>
  );
}
